import React, { useEffect, useState } from "react";
import { Amplify, Auth } from "aws-amplify";
import { amplifyConfig } from "./config";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { endpoints, getPath } from "./api/apiUtils";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

dayjs.extend(timezone);
Amplify.configure(amplifyConfig);

export const AuthContext = React.createContext({
  authenticated: undefined,
  validating: undefined,
  isEdifyAdmin: false,
  user: undefined,
  login: () => undefined,
  logout: () => undefined,
  getAccessToken: () => undefined,
  forgotPassword: () => undefined,
  resetPassword: () => undefined,
  confirmSignup: () => undefined,
  signUp: () => undefined,
});

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [isEdifyAdmin, setIsEdifyAdmin] = useState(false);
  const [user, setUser] = useState();
  const [userId, setUserId] = useState();
  const [validating, setValidating] = useState(true);

  useEffect(() => {
    checkSession();
  }, []);

  const { data: userData } = useQuery(
    ["users", userId],
    async () =>
      axios
        .get(getPath({ endpoint: endpoints.users, path: [userId] }), {
          headers: { Authorization: await getAccessToken() },
        })
        .then((r) => r.data),
    { enabled: !!userId }
  );

  useEffect(() => {
    if (userData?.User) setUser(userData.User);
  }, [userData]);

  const checkSession = async () => {
    return Auth.currentSession()
      .then(async (session) => {
        if (!session.isValid) {
          setAuthenticated(false);
        } else {
          setAuthenticated(true);
        }
        const {
          idToken: { payload },
        } = session;
        await setSessionData(payload);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => setValidating(false));
  };

  const setSessionData = async (payload) => {
    const groups = payload["cognito:groups"];
    if (groups && groups.includes("superadmin")) {
      setIsEdifyAdmin(true);
    }
    setUserId(payload.sub);
  };

  const getAccessToken = async () => {
    return Auth.currentSession()
      .then((session) => {
        return session.getAccessToken().getJwtToken();
      })
      .catch((err) => {
        console.error(err);
        Auth.signOut();
      });
  };

  const navigate = useNavigate();
  const userContext = {
    authenticated: authenticated,
    login: async ({ email, password, redirect }) => {
      return Auth.signIn(email, password).then(() => {
        setAuthenticated(true);
        checkSession().then(() => {
          redirect && navigate(redirect);
        });
      });
    },
    logout: async () => {
      Auth.signOut().then(() => {
        setAuthenticated(false);
        setUser();
        setUserId();
      });
    },
    signUp(username, password, firstName, lastName, organizationId) {
      return Auth.signUp({
        username,
        password,
        attributes: {
          name: firstName,
          family_name: lastName,
          zoneinfo: dayjs.tz.guess(),
          "custom:organization_id": organizationId,
        },
      });
    },
    confirmSignup(email, verification) {
      return Auth.confirmSignUp(email, verification);
    },
    forgotPassword(email) {
      return Auth.forgotPassword(email);
    },
    resetPassword(email, password, code) {
      console.log("resetting password");
      return Auth.forgotPasswordSubmit(email, code, password);
    },
    getAccessToken: async () => {
      return await getAccessToken();
    },
    isEdifyAdmin: isEdifyAdmin,
    userId: userId,
    user: user,
  };

  return validating ? null : (
    <AuthContext.Provider value={userContext}>{children}</AuthContext.Provider>
  );
};
